// 🚀 CAROUSEL MENU EXPERT - OPTIMISÉ PERFORMANCE & UX
// Fixes critiques: Conflits scroll, transitions fluides, gestion responsive

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { gsap } from 'gsap';
import { detectPerformanceCapabilities } from '../utils/performanceUtils';

const MenuCarouselFixed = ({ 
  images, 
  activeSection, 
  isActive = true,
  currentImageIndex, 
  setCurrentImageIndex, 
  isUserInteracting, 
  setIsUserInteracting 
}) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const imageRef2 = useRef(null);
  const [activeImageRef, setActiveImageRef] = useState(1); // 1 ou 2
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastMoveTime = useRef(Date.now());
  const autoPlayTimer = useRef(null);
  const isPausedRef = useRef(false);
  
  // 🎯 Performance configuration
  const perf = useMemo(() => detectPerformanceCapabilities(), []);
  
  // 🔄 Navigation optimisée avec debounce
  const navigateToImage = useCallback((direction) => {
    if (isDragging) return; // Éviter les conflits
    
    setIsUserInteracting(true);
    const newIndex = direction === 'next' 
      ? (currentImageIndex + 1) % images.length
      : (currentImageIndex - 1 + images.length) % images.length;
    
    setCurrentImageIndex(newIndex);
    
    // Reset interaction state avec délai adaptatif
    setTimeout(() => setIsUserInteracting(false), perf.isMobile ? 1500 : 2500);
  }, [currentImageIndex, images.length, isDragging, setCurrentImageIndex, setIsUserInteracting, perf.isMobile]);

  // 🎨 Animation de fondu croisé avec deux images fixes
  const animateImageTransition = useCallback((newIndex) => {
    if (!imageRef.current || !imageRef2.current || !images[newIndex]) return;

    // Annuler toutes les animations en cours
    gsap.killTweensOf([imageRef.current, imageRef2.current]);

    const currentRef = activeImageRef === 1 ? imageRef.current : imageRef2.current;
    const nextRef = activeImageRef === 1 ? imageRef2.current : imageRef.current;

    // Préparer la nouvelle image (invisible)
    gsap.set(nextRef, {
      backgroundImage: `url(${images[newIndex].src})`,
      opacity: 0,
      zIndex: 2
    });

    // Fondu croisé simple et fluide
    gsap.to(nextRef, {
      opacity: 1,
      duration: 0.5,
      ease: "power1.inOut",
      onComplete: () => {
        // Basculer les références et nettoyer
        gsap.set(currentRef, { zIndex: 1 });
        gsap.set(nextRef, { zIndex: 2 });
        setActiveImageRef(activeImageRef === 1 ? 2 : 1);
      }
    });
  }, [images, activeImageRef]);

  // ▶️ Auto-play avec pauses intelligentes
  const stopAutoPlay = useCallback(() => {
    if (autoPlayTimer.current) {
      clearInterval(autoPlayTimer.current);
      autoPlayTimer.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    // Sur mobile, pas d'auto-play pour éviter les conflits avec le scroll
    if (perf.isMobile) return;
    
    if (autoPlayTimer.current) return;
    const delay = 3500;
    autoPlayTimer.current = setInterval(() => {
      if (!isPausedRef.current && !isDragging && isActive) {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }
    }, delay);
  }, [images.length, isDragging, setCurrentImageIndex, isActive, perf.isMobile]);

  // Pause on visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        stopAutoPlay();
      } else {
        startAutoPlay();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [startAutoPlay, stopAutoPlay]);

  // Pause on hover/touch and user interaction
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onEnter = () => { isPausedRef.current = true; };
    const onLeave = () => { isPausedRef.current = false; };
    container.addEventListener('mouseenter', onEnter, { passive: true });
    container.addEventListener('mouseleave', onLeave, { passive: true });
    return () => {
      container.removeEventListener('mouseenter', onEnter);
      container.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // Démarrer au montage et à chaque changement de set d'images
  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [startAutoPlay, stopAutoPlay, images.length, isActive]);

  // 🖱️ Gestion tactile expert avec momentum
  const handlePointerStart = useCallback((e) => {
    // Sur mobile, ne rien faire - permettre le scroll naturel
    if (perf.isMobile) return;
    
    const pointer = e.touches ? e.touches[0] : e;
    setIsDragging(true);
    setStartPos({ x: pointer.clientX, y: pointer.clientY });
    setCurrentPos({ x: pointer.clientX, y: pointer.clientY });
    lastMoveTime.current = Date.now();
    isPausedRef.current = true; // pause autoplay pendant drag/touch
    
    // Arrêter les animations en cours
    gsap.killTweensOf(imageRef.current);
  }, [perf.isMobile]);

  const handlePointerMove = useCallback((pointer) => {
    // Sur mobile, ne rien faire
    if (perf.isMobile || !isDragging) return;
    
    const now = Date.now();
    const deltaTime = now - lastMoveTime.current;
    
    if (deltaTime > 0) {
      velocityRef.current = {
        x: (pointer.clientX - currentPos.x) / deltaTime,
        y: (pointer.clientY - currentPos.y) / deltaTime
      };
    }
    
    setCurrentPos({ x: pointer.clientX, y: pointer.clientY });
    lastMoveTime.current = now;

    // Feedback visuel minimal pendant le drag (seulement sur desktop)
    if (imageRef.current) {
      const deltaX = pointer.clientX - startPos.x;
      gsap.set(imageRef.current, {
        x: deltaX * 0.1, // Résistance subtile
        force3D: true
      });
    }
  }, [isDragging, startPos, currentPos, perf.isMobile]);

  const handlePointerEnd = useCallback(() => {
    // Sur mobile, ne rien faire
    if (perf.isMobile || !isDragging) return;
    
    setIsDragging(false);
    const deltaX = currentPos.x - startPos.x;
    const deltaY = currentPos.y - startPos.y;
    const velocity = Math.abs(velocityRef.current.x);
    
    // Déterminer si c'est un swipe valide
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isSignificantMove = Math.abs(deltaX) > 50 || velocity > 0.5;
    
    if (isHorizontalSwipe && isSignificantMove) {
      // Navigation basée sur direction + vélocité
      const direction = deltaX > 0 ? 'prev' : 'next';
      navigateToImage(direction);
    }
    
    // Reset position avec animation simple (seulement sur desktop)
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        x: 0,
        duration: 0.3,
        ease: "power2.out",
        force3D: true
      });
    }
    setTimeout(() => { isPausedRef.current = false; }, 600);
  }, [isDragging, currentPos, startPos, navigateToImage, perf.isMobile]);

  // 🎚️ Gestion de la molette optimisée
  const handleWheel = useCallback((e) => {
    // Ne pas bloquer le scroll sur mobile
    if (perf.isMobile) return;
    
    e.preventDefault();
    
    // Throttling pour éviter le spam
    const now = Date.now();
    if (now - lastMoveTime.current < 150) return;
    lastMoveTime.current = now;
    
    const direction = e.deltaY > 0 ? 'next' : 'prev';
    navigateToImage(direction);
  }, [navigateToImage, perf.isMobile]);

  // ⚡ Setup des event listeners optimisé
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Options optimisées pour les événements
    const passiveOptions = { passive: false };
    const passiveTrue = { passive: true };

    // Sur mobile, AUCUN événement tactile pour permettre le scroll naturel
    if (perf.isMobile) {
      // Pas d'événements tactiles sur mobile - scroll libre
      return () => {};
    } else {
      // Mouse events (desktop seulement)
      container.addEventListener('mousedown', handlePointerStart, passiveTrue);
      
      // Global mouse events pour drag
      const handleGlobalMouseMove = (e) => {
        if (isDragging) handlePointerMove(e);
      };
      const handleGlobalMouseUp = () => {
        if (isDragging) handlePointerEnd();
      };

      document.addEventListener('mousemove', handleGlobalMouseMove, passiveTrue);
      document.addEventListener('mouseup', handleGlobalMouseUp, passiveTrue);
      
      // Wheel events avec throttling (desktop seulement)
      container.addEventListener('wheel', handleWheel, passiveOptions);

      return () => {
        container.removeEventListener('mousedown', handlePointerStart);
        container.removeEventListener('wheel', handleWheel);
        
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [handlePointerStart, handlePointerMove, handlePointerEnd, handleWheel, isDragging, perf.isMobile]);

  // 🔄 Correction de l'index si invalide (après changement d'images)
  useEffect(() => {
    if (images.length > 0 && currentImageIndex >= images.length) {
      const validIndex = Math.max(0, images.length - 1);
      setCurrentImageIndex(validIndex); // Reset à la dernière image valide
      return;
    }
    if (images.length > 0) {
      animateImageTransition(currentImageIndex);
    }
  }, [currentImageIndex, animateImageTransition, images.length, setCurrentImageIndex]);

  // 🔄 Force mise à jour lors du changement d'images
  useEffect(() => {
    if (images.length > 0) {
      const safeIndex = Math.min(currentImageIndex, images.length - 1);
      if (safeIndex !== currentImageIndex) {
        setCurrentImageIndex(safeIndex);
      }
    }
  }, [images, currentImageIndex, setCurrentImageIndex]);

  return (
    <div 
      ref={containerRef}
      className="menu-carousel-container"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none' // Prévenir les gestes natifs
      }}
    >
      {/* Première image */}
      <div
        ref={imageRef}
        className="menu-carousel-image"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${images[currentImageIndex]?.src || ''})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderRadius: '30px',
          willChange: 'transform, opacity',
          transform: 'translateZ(0)',
          zIndex: activeImageRef === 1 ? 2 : 1
        }}
      />
      
      {/* Deuxième image pour transitions */}
      <div
        ref={imageRef2}
        className="menu-carousel-image"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${images[currentImageIndex]?.src || ''})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderRadius: '30px',
          willChange: 'transform, opacity',
          transform: 'translateZ(0)',
          opacity: 0,
          zIndex: activeImageRef === 2 ? 2 : 1
        }}
      />

      {/* Indicateur de progression */}
      <div className="menu-carousel-indicator">
        {Math.min(currentImageIndex + 1, images.length)} / {images.length}
      </div>

      {/* Boutons de navigation */}
      <button 
        className="menu-carousel-nav menu-carousel-prev"
        onClick={() => navigateToImage('prev')}
        style={{
          position: 'absolute',
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          color: 'var(--brand-pink)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'white';
          e.target.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
          e.target.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        ‹
      </button>

      <button 
        className="menu-carousel-nav menu-carousel-next"
        onClick={() => navigateToImage('next')}
        style={{
          position: 'absolute',
          right: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          color: 'var(--brand-pink)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'white';
          e.target.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
          e.target.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        ›
      </button>
    </div>
  );
};

export default MenuCarouselFixed;